import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Check, Loader2, Clock, X, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { openCheckout } from "@/lib/checkout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import KYCSection from "@/components/KYCSection";

const STATUS_META = {
  pending: { label: "Under review", color: "text-amber-400", Icon: Clock },
  approved: { label: "Approved", color: "text-emerald-400", Icon: Check },
  rejected: { label: "Not approved", color: "text-red-400", Icon: X },
};

export default function Verification() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ price: 299, application: null, is_verified: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/verification/status");
    setState(data);
  };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    setBusy(true);
    try {
      const { data: order } = await api.post("/verification/create-order");
      await openCheckout(order, async (payload) => {
        await api.post("/verification/verify", payload);
        toast.success("Payment received. Review usually takes 24-48 hours.");
        await load();
        await refresh();
      });
    } catch (err) {
      if (err?.message !== "Payment cancelled") {
        toast.error(err?.response?.data?.detail || err?.message || "Payment failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const status = state.application?.status;
  const meta = status ? STATUS_META[status] : null;

  return (
    <div data-testid="verification-page" className="min-h-screen pb-24">
      <TopBar subtitle="Verified badge"/>
      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-4">
        <BackButton fallback="/app/me" label="Back"/>
        <div className="rounded-3xl border border-[#DC2626]/30 bg-gradient-to-br from-[#DC2626]/12 via-[#180D0D] to-[#0A0605] p-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#DC2626]/20 blur-3xl"/>
          <div className="w-12 h-12 rounded-2xl bg-[#DC2626] text-black flex items-center justify-center">
            <ShieldCheck size={22}/>
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold">The <span className="gold-text">gold checkmark</span>.</h1>
          <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
            A verified badge tells casting directors your profile is legit — real name, real face, real craft. Once approved, your videos rank higher in discovery.
          </p>

          {state.is_verified ? (
            <div data-testid="already-verified" className="mt-5 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 space-y-3">
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold"><Check size={16}/> You're verified</div>
              <p className="text-xs text-zinc-300">The gold badge is live on your profile.</p>
              <Link to="/app/story-card" data-testid="open-story-card" className="inline-flex items-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold px-4 h-10 text-sm">
                <Sparkles size={14}/> Get your story card
              </Link>
            </div>
          ) : status === "pending" ? (
            <div data-testid="verification-pending" className="mt-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4">
              <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold"><Clock size={16}/> Under review</div>
              <p className="text-xs text-zinc-400 mt-1">Our team is reviewing your profile. You'll hear back within 48 hours.</p>
            </div>
          ) : (
            <>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold">₹{state.price}</span>
                <span className="text-xs text-zinc-400">/ month</span>
              </div>
              <Button data-testid="apply-verification" onClick={apply} disabled={busy} className="mt-4 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-black font-semibold h-11 px-6">
                {busy ? <><Loader2 className="animate-spin mr-2" size={16}/> Processing…</> : `Apply — pay ₹${state.price}`}
              </Button>
              {status === "rejected" && (
                <p className="mt-3 text-xs text-red-400">Previous application was not approved. Feel free to try again.</p>
              )}
            </>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <p className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Sparkles size={16} className="text-[#DC2626]"/> What you get</p>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li className="flex items-start gap-2"><Check size={14} className="text-[#DC2626] mt-1"/> Gold verified checkmark next to your name, everywhere</li>
            <li className="flex items-start gap-2"><Check size={14} className="text-[#DC2626] mt-1"/> Priority ranking in Discovery and search</li>
            <li className="flex items-start gap-2"><Check size={14} className="text-[#DC2626] mt-1"/> Your profile shortlisted for handpicked casting briefs</li>
            <li className="flex items-start gap-2"><Check size={14} className="text-[#DC2626] mt-1"/> Manual review by our team — genuine talents only</li>
          </ul>
        </div>

        <KYCSection/>

        {meta && status !== "pending" && (
          <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
            <p className="text-xs text-zinc-400">Last application</p>
            <div className={`mt-1 flex items-center gap-2 text-sm ${meta.color}`}>
              <meta.Icon size={14}/> {meta.label}
            </div>
            {state.application?.rejection_reason && (
              <p className="text-xs text-zinc-400 mt-1">Reason: {state.application.rejection_reason}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
